'use client';

import { useEffect, useState } from 'react';

const SUBSCRIBE_HREF = '/auth/register?plan=spark';

/**
 * Within-page A/B (PostHog multivariate flag `lp_layout_variant`):
 *   - control 'A' (default / flag absent): render nothing — hero "Get started"
 *     + the pricing cards are the only CTAs.
 *   - variant 'B': add a persistent bottom "Subscribe — $99/mo" band. Hypothesis:
 *     buy-ready paid clickers convert better with an explicit price CTA than the
 *     hero's voice-first path.
 * Fully additive — does not touch the protected Hero. Fires `lp_variant_assigned`
 * once and `lp_cta_click` on click. Falls back to control if PostHog is absent.
 */
export function StickySubscribeCta({ lpKey }: { lpKey: string }) {
  // Default to control 'A' (render nothing) so we never synchronously set state
  // in the effect body; only the PostHog feature-flag subscription updates it.
  const [variant, setVariant] = useState<'A' | 'B'>('A');

  useEffect(() => {
    type Ph = {
      getFeatureFlag?: (k: string) => string | boolean | undefined;
      onFeatureFlags?: (cb: () => void) => void;
      capture?: (n: string, p?: Record<string, unknown>) => void;
    };
    const ph = (window as unknown as { posthog?: Ph }).posthog;
    if (!ph?.onFeatureFlags) return; // no PostHog → stay on control
    let assigned = false;
    // Subscribe to flag resolution (external system); PostHog invokes this when
    // flags load (and immediately if already loaded).
    ph.onFeatureFlags(() => {
      const v = ph.getFeatureFlag?.('lp_layout_variant') === 'B' ? 'B' : 'A';
      setVariant(v);
      if (!assigned) {
        assigned = true;
        ph.capture?.('lp_variant_assigned', { lp_key: lpKey, flag: 'lp_layout_variant', variant: v });
      }
    });
  }, [lpKey]);

  if (variant !== 'B') return null;

  const onClick = () => {
    try {
      (window as unknown as { posthog?: { capture?: (n: string, p?: Record<string, unknown>) => void } }).posthog?.capture?.('lp_cta_click', {
        lp_key: lpKey,
        cta: 'subscribe_sticky',
        variant: 'B',
      });
    } catch {
      /* noop */
    }
  };

  return (
    <div
      data-testid="lp-sticky-subscribe"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        display: 'flex',
        justifyContent: 'center',
        padding: '12px 16px',
        background: 'color-mix(in srgb, var(--ink) 82%, transparent)',
        backdropFilter: 'blur(12px)',
        borderTop: '1px solid var(--hair)',
      }}
    >
      <a
        href={SUBSCRIBE_HREF}
        onClick={onClick}
        className="v4-cta-primary"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 'min(100%, 420px)',
          fontFamily: 'var(--font-geist-sans)',
          fontWeight: 600,
          fontSize: 16,
          letterSpacing: '-0.01em',
          color: 'var(--ink)',
          background: 'linear-gradient(135deg, var(--cyan-glow) 0%, var(--paper) 100%)',
          borderRadius: 999,
          padding: '14px 28px',
          boxShadow: '0 10px 30px -10px rgba(125,250,255,0.55)',
        }}
      >
        Subscribe — $99/mo
      </a>
    </div>
  );
}
